/**
 * Löschen — für jede Sammlung derselbe Weg.
 *
 * ## Was „löschen" hier heißt
 *
 * **Stilllegen**, nicht entfernen: `aktiv` geht auf `false`. Die Zeile
 * verschwindet aus jeder Liste (`festeBedingung: 'Aktiv = 1'` in
 * `apps/api/src/sammlungen.ts`), bleibt aber als Nachweis stehen. Dieselbe
 * Regel, nach der `fortschreiben.mjs` seit dem 11.08.2026 arbeitet und nach
 * der „Entfernen" in der Rollenmatrix seit dem 13.08.2026 läuft.
 *
 * Der Kunde hat am 26.08.2026 einen Löschknopf „überall" verlangt und dazu
 * gesagt: „Das muss natürlich auch mit einer Freigabe wieder passieren."
 * Genau das ergibt sich von selbst — Stilllegen ist eine Feldänderung, und ob
 * sie durch eine Freigabe muss, entscheidet `iam.Freigabepflicht`. Seit `042`
 * stehen dort auch Gruppen und Organisationseinheiten.
 *
 * ## Warum ein eigener Name
 *
 * `satzStilllegen(...)` statt `satzAendern(..., { aktiv: false })`: die
 * Absicht steht damit an der Aufrufstelle und nicht in einem Wert. Dieselbe
 * Überlegung wie bei `zuordnungStilllegen`, das es seit dem 13.08.2026 gibt —
 * diese Datei verallgemeinert es auf jede schreibbare Sammlung.
 *
 * ## Der Grund ist Pflicht
 *
 * Ohne Angabe verschwindet nichts. Bei einer freigabepflichtigen Objektart
 * liest ihn der Mensch, der entscheidet; bei den übrigen steht er im
 * Protokoll. Ein Löschvorgang ohne Begründung ist ein Eintrag, der die eine
 * Frage nicht beantwortet, für die er geführt wird.
 */

import { senden } from './klient.js';
import { tokenHolen } from '../anmeldung/msal.js';
import type { Schreibergebnis } from './rollen.js';

/** Mindestlänge des Grundes. Wie in der Rollenmatrix seit dem 13.08.2026. */
export const GRUND_MINDESTENS = 5;

/**
 * Legt einen Satz still.
 *
 * `version` ist die Zeilenversion aus der gelesenen Zeile und geht als
 * `If-Match` mit; ohne sie weist die Schnittstelle ab. Das ist kein
 * Formalismus: zwischen Lesen und Löschen kann jemand anders geschrieben
 * haben, und dann bezieht sich die Entscheidung auf einen Stand, den es nicht
 * mehr gibt.
 */
export async function satzStilllegen(
  sammlung: string,
  mandant: string,
  schluessel: string,
  version: string,
  begruendung: string,
): Promise<Schreibergebnis> {
  return senden<Schreibergebnis>(
    `/api/${sammlung}/${encodeURIComponent(schluessel)}`,
    'PATCH',
    {
      mandant,
      token: await tokenHolen(),
      rumpf: { aenderungen: { aktiv: false }, begruendung },
      zusatzkopf: { 'if-match': version },
    },
  );
}

/** Ergebnis eines Massenlöschens — je Objekt einzeln, nicht als Summe. */
export interface Massenergebnis {
  /** Sofort stillgelegt. */
  geloescht: string[];
  /** Zur Freigabe eingereicht — noch nicht wirksam. */
  eingereicht: string[];
  /** Mit Grund gescheitert. */
  gescheitert: { schluessel: string; grund: string }[];
}

/**
 * Legt mehrere Sätze still und meldet **je Objekt**, was daraus wurde.
 *
 * Nacheinander und nicht nebenläufig: jede Änderung läuft serverseitig in
 * einer eigenen Transaktion, und zwanzig gleichzeitige Schreibvorgänge auf
 * denselben Tabellen erzeugen Sperrkonflikte, die wie Fehler der Eingabe
 * aussehen.
 *
 * **Teilerfolge werden einzeln genannt.** Die Massenaktionsleiste der
 * Rollenmatrix hat bis zum 26.08.2026 „N Berechtigungen betroffen.
 * Teilerfolge werden je Objekt einzeln gemeldet" gemeldet — und dabei nichts
 * getan. Diese Funktion tut, was der Satz behauptet hat.
 */
export async function saetzeStilllegen(
  sammlung: string,
  mandant: string,
  saetze: { schluessel: string; version: string }[],
  begruendung: string,
): Promise<Massenergebnis> {
  const ergebnis: Massenergebnis = { geloescht: [], eingereicht: [], gescheitert: [] };
  for (const s of saetze) {
    try {
      const antwort = await satzStilllegen(sammlung, mandant, s.schluessel, s.version, begruendung);
      /* `freigabe.eingereicht` heisst: nichts ist geschrieben, es wartet auf
         eine zweite Person. Der Unterschied ist an `geaendert: []` ablesbar
         und gehoert in die Meldung — „Geloescht" waere hier eine
         Falschauskunft. */
      if (antwort.freigabe?.eingereicht) ergebnis.eingereicht.push(s.schluessel);
      else ergebnis.geloescht.push(s.schluessel);
    } catch (fehler) {
      ergebnis.gescheitert.push({
        schluessel: s.schluessel,
        grund: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
  }
  return ergebnis;
}
